import {
    DS_COLOR,
    DS_HEIGHT,
    DS_SPACING_UNITS,
} from '@calm/evexia-design-tokens';
import { MediaProgram, MediaProgramGuide } from '@calm/evexia-web-sdk';
import Card, { CardProps } from '@rhg/components/card';
import { CardVariant } from '@rhg/components/card/types';
import { useHumanReadable } from '@rhg/hooks/use-human-readable';
import { useProvidedProgramContext } from '@rhg/hooks/use-provided-program-context';
import commonMessages from '@rhg/lang/messages/common';
import programMessages from '@rhg/lang/messages/program';
import { Box, SvgIcon, SxProps, Theme } from '@rhg/libs/material';
import { PlayFilled } from '@rhg/libs/material/icons';
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

const styleBottomIcon = {
    borderRadius: '50%',
    backgroundColor: (theme: Theme) =>
        theme.palette.mode === 'light'
            ? theme.palette.mask.black['60p']
            : theme.palette.mask.white['80p'],
    padding: DS_SPACING_UNITS.BASE,
    color: 'primary.contrastText',
    m: 2,
};

const styleAutoplayIconWrapper = {
    position: 'relative',
    height: '24px',
    width: '24px',
    m: 2,
    borderRadius: '50%',
    backgroundColor: (theme: Theme) =>
        theme.palette.mode === 'light'
            ? theme.palette.mask.black['60p']
            : theme.palette.mask.white['80p'],

    '& .timerSvg': {
        overflow: 'visible',
        position: 'absolute',
        top: '2px',
        right: '2px',
        height: '20px',
        width: '20px',
        zIndex: 2,
    },
    '& .timerCircle': {
        fill: 'none',
        strokeWidth: '2px',
        stroke: (theme: Theme) =>
            theme.palette.mode === 'light'
                ? DS_COLOR.WHITE_100P
                : DS_COLOR.BLACK_100P,
        transform: 'rotate(270deg)',
        transition: '1s linear all',
        transformOrigin: 'center',
        fillRule: 'nonzero',
    },
};

const styleAutoplayIcon = {
    position: 'absolute',
    top: '3px',
    left: '4px',
    height: '17px',
    width: '17px',
    m: 0,

    path: {
        fill: (theme: Theme) =>
            theme.palette.mode === 'light'
                ? DS_COLOR.WHITE_100P
                : DS_COLOR.BLACK_100P,
    },
};

type HorizontalMinimalGuideCardProps = {
    guide: MediaProgramGuide;
    program?: MediaProgram;
    variant?: CardVariant;
    onClick: (guide: MediaProgramGuide) => void;
    preferNarratorImage?: boolean;
    includePositionInTitle?: boolean;
    bottomIconOverride?: ReactNode;
    sx?: SxProps<Theme>;
} & Omit<Partial<CardProps>, 'onClick'>;
/** Don't export this, use TrailerCard or NextStepCard instead */
export function DontUseHorizontalMinimalGuideCard({
    guide,
    preferNarratorImage = false,
    onClick,
    sx,
    program: _programProp,
    bottomIconOverride,
    includePositionInTitle,
    ...otherProps
}: HorizontalMinimalGuideCardProps) {
    const { formatMessage } = useIntl();
    const providedContext = useProvidedProgramContext();
    const program = _programProp ?? providedContext.program;

    const imageUrl = useMemo(() => {
        if (preferNarratorImage) {
            return (
                program?.narrator?.headshot?.url ??
                program?.backgroundImage?.url ??
                ''
            );
        } else {
            return (
                guide?.image?.url ??
                program?.narrator?.headshot?.url ??
                program?.backgroundImage?.url ??
                ''
            );
        }
    }, [
        guide?.image?.url,
        preferNarratorImage,
        program?.backgroundImage?.url,
        program?.narrator?.headshot?.url,
    ]);

    const playGuide = useCallback(() => {
        onClick(guide);
    }, [guide, onClick]);

    const title = includePositionInTitle
        ? `${guide.position}. ${guide?.title}`
        : guide.title || '';

    return (
        <Card
            isEntireCardClickable
            variant="horizontal"
            title={title}
            imageAlt=""
            imageSrc={imageUrl}
            imageIconBottom={
                bottomIconOverride ?? (
                    <SvgIcon
                        aria-label={formatMessage(
                            commonMessages['common.play'],
                            { title: guide.title }
                        )}
                        sx={styleBottomIcon}
                    >
                        <PlayFilled />
                    </SvgIcon>
                )
            }
            imageShowIconsOnFocused
            onClick={playGuide}
            label={formatMessage(commonMessages['common.play'], {
                title: guide?.title,
            })}
            trackButton="GuideCardPlay"
            backgroundColor="transparent"
            sx={sx}
            imageForcedSize={DS_HEIGHT.HORIZONTAL_CARD_SIZE}
            {...otherProps}
        />
    );
}

export function TrailerCard({
    onPlay,
    guide,
    preferNarratorImage,
}: {
    onPlay: () => void;
    guide: MediaProgramGuide;
    preferNarratorImage?: boolean;
}) {
    const { duration } = useHumanReadable();
    return (
        <DontUseHorizontalMinimalGuideCard
            guide={guide}
            onClick={onPlay}
            preferNarratorImage={preferNarratorImage}
            categoryDescription={duration(guide?.totalDuration)}
        />
    );
}

const styleFullWidth = {
    width: '100%',
};

export function ScheduleCard({
    onPlay,
    guide,
    bottomIconOverride,
}: {
    onPlay: () => void;
    guide: MediaProgramGuide;
    bottomIconOverride?: ReactNode;
}) {
    const { duration } = useHumanReadable();
    const { formatMessage } = useIntl();

    const progressTextAlt = useMemo(() => {
        if (guide?.totalDuration && guide?.progressDuration) {
            return formatMessage(commonMessages['common.percent_complete'], {
                percentage: guide?.progressDuration
                    ? Math.round(
                          (guide?.progressDuration / guide?.totalDuration) * 100
                      )
                    : 0,
            });
        }
    }, [guide?.totalDuration, guide?.progressDuration, formatMessage]);

    const progress = useMemo(() => {
        if (!guide.progressDuration) {
            return;
        }

        if (
            guide?.totalDuration &&
            (guide?.progressDuration || guide?.progressDuration === 0)
        ) {
            return guide?.progressDuration / guide?.totalDuration;
        }

        return 0;
    }, [guide?.totalDuration, guide?.progressDuration]);

    return (
        <DontUseHorizontalMinimalGuideCard
            guide={guide}
            onClick={onPlay}
            categoryDescription={duration(guide?.totalDuration)}
            bottomIconOverride={bottomIconOverride}
            isEntireCardClickable
            includePositionInTitle
            progress={progress}
            progressTextAlt={progressTextAlt}
            sx={styleFullWidth}
        />
    );
}

const AUTOSTART_DURATION = 5;
export function NextStepCard({
    onClick,
    guide,
}: {
    onClick: () => void;
    guide: MediaProgramGuide;
}) {
    const [autoStartSeconds, setAutoStartSeconds] = useState(0);

    useEffect(() => {
        let _autostartSeconds = 0;
        const intervalId = setInterval(() => {
            if (_autostartSeconds < AUTOSTART_DURATION) {
                _autostartSeconds += 1;
                setAutoStartSeconds(_autostartSeconds);
            }

            if (_autostartSeconds === AUTOSTART_DURATION) {
                onClick();
            }
        }, 1000);

        return () => clearInterval(intervalId);
    }, [onClick]);

    const { formatMessage } = useIntl();
    return (
        <DontUseHorizontalMinimalGuideCard
            guide={guide}
            onClick={onClick}
            categoryDescription={
                AUTOSTART_DURATION - autoStartSeconds === 1
                    ? formatMessage(
                          programMessages[
                              'program.next_session_will_start_one_second'
                          ]
                      )
                    : formatMessage(
                          programMessages['program.next_session_will_start'],
                          {
                              autoStartSeconds:
                                  AUTOSTART_DURATION - autoStartSeconds,
                          }
                      )
            }
            bottomIconOverride={
                <Box sx={styleAutoplayIconWrapper}>
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        className="timerSvg"
                    >
                        <circle
                            cx="10"
                            cy="10"
                            r="10"
                            className="timerCircle"
                            pathLength="360"
                            strokeDasharray={`${
                                ((autoStartSeconds + 1) / AUTOSTART_DURATION) *
                                360
                            } 360`}
                        />
                    </svg>
                    <PlayFilled sx={styleAutoplayIcon} />
                </Box>
            }
        />
    );
}
